import { useState, useEffect, useCallback } from 'react';


interface LoadingState {
    isLoading: boolean;
    message: string;
}

interface PlanUpdatingState {
    isUpdating: boolean;
    idPlan: string;
}

const LEXIA_API_BASE_URL = (window as any).env.REACT_APP_LEXIA_API_BASE_URL;

const useSOCopilotAdminPrompts = (feature, languageCode, extensionId) => {
    const [prompts, setPrompts] = useState(null);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState<LoadingState>({ isLoading: false, message: '' });
    const [planUpdating, setPlanUpdating] = useState<PlanUpdatingState>({ isUpdating: false, idPlan: '' });
    const [error, setError] = useState(null);

    useEffect(() => {

        const fetchPlans = async () => {
            setLoading({ isLoading: true, message: 'Loading subscription plans...' });
            setError(null);

            try {
                const accessToken = localStorage.getItem('accessToken');

                if (!accessToken) {
                    throw new Error("Access token is missing");
                }

                const response = await fetch(`${LEXIA_API_BASE_URL}/SOCopilotAdmin/GetExtensionSubscriptionPlans?extensionId=${extensionId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`Erreur : ${response.status} ${response.statusText}`);
                }

                const result = await response.json();
                console.log('Fetched Plans:', result);

                setPlans(result);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading({ isLoading: false, message: 'Subscription plans loaded' });
            }
        };

        if (extensionId) {
            fetchPlans();
        }
    }, [extensionId]);

    useEffect(() => {
        const fetchPrompt = async () => {

            // setLoading(true);
            setLoading({ isLoading: true, message: 'Loading prompt in progress...' });
            setError(null);

            try {
                // Exemple d'accès au token. Remplacez-le par la méthode appropriée si nécessaire.
                const accessToken = localStorage.getItem('accessToken');

                if (!accessToken) {
                    throw new Error("Access token is missing");
                }

                const response = await fetch(`${LEXIA_API_BASE_URL}/SOCopilotAdmin/GetPrompt?Feature=${feature}&LanguageCode=${languageCode}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`Erreur : ${response.status} ${response.statusText}`);
                }

                const result = await response.json();
                console.log('result :', result);
                setPrompts(result); // On stocke les données récupérées dans l'état
            } catch (err) {
                setError(err.message);
            } finally {
                //setLoading(false);
                setLoading({ isLoading: false, message: 'Prompt loaded...' });
            }
        };

        if (feature) { 
            fetchPrompt();
        }
    }, [feature, languageCode]);


    // Méthode POST pour envoyer un test
    const sendTest = useCallback(async ({ languageCode, systemChatMessage, userChatMessage, message }) => {
 
        setLoading({ isLoading: true, message: 'AI interrogation in progress...' });
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            if (!accessToken) {
                throw new Error("Access token is missing");
            }

            const response = await fetch(`${LEXIA_API_BASE_URL}/SOCopilotAdmin/TestPrompt`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    languageCode,
                    systemChatMessage,
                    userChatMessage,
                    message
                }),
            });

            if (!response.ok) {
                throw new Error(`Erreur : ${response.status} ${response.statusText}`);
            }

            const result = await response.json();
            console.log('Test result:', result);
            return result;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {

            setLoading({ isLoading: false, message: 'AI interrogation ended' });
        }
    }, []);

    const updatePrompt = useCallback(async ({ feature, languageCode, systemChatMessage, userChatMessage }) => {

        setLoading({ isLoading: true, message: 'Prompt update in progress...' });
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            if (!accessToken) {
                throw new Error("Access token is missing");
            }

            const response = await fetch(`${LEXIA_API_BASE_URL}/SOCopilotAdmin/UpdatePrompt`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    feature,
                    languageCode: languageCode,
                    systemChatMessage,
                    userChatMessage
                }),
            });

            if (!response.ok) {
                throw new Error(`Erreur : ${response.status} ${response.statusText}`);
            }

        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading({ isLoading: false, message: 'Prompt updated' });
        }
    }, []);

    const createOrUpdatePlan = useCallback(async ({
        id,
        code,
        name,
        tokenAllowed,
        tokenInpoutEstimate,
        tokenOutpoutEstimate,
        costEstimate,
        queryAllowed,
        gptDeploymentName,
        maxOutputTokensAllowedByQuery,
    }: {
        id: string;
        code: string;
        name: string;
        tokenAllowed: number;
        tokenInpoutEstimate?: number;
        tokenOutpoutEstimate?: number;
        costEstimate?: number;
        queryAllowed: number;
        gptDeploymentName?: string;
        maxOutputTokensAllowedByQuery?: number;
    }) => {

        //debugger
    
        setPlanUpdating({ isUpdating: true, idPlan: id });
        setError(null);
    
        try {
            const accessToken = localStorage.getItem('accessToken');
    
            if (!accessToken) {
                throw new Error("Access token is missing");
            }
    
            const response = await fetch(`${LEXIA_API_BASE_URL}/SOCopilotAdmin/SetExtensionSubscriptionPlans`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id,
                    code,
                    name,
                    tokenAllowed,
                    tokenInpoutEstimate,
                    tokenOutpoutEstimate,
                    costEstimate,
                    queryAllowed,
                    gptDeploymentName,
                    maxOutputTokensAllowedByQuery
                }),
            });
    
            if (!response.ok) {
                throw new Error(`Error: ${response.status} ${response.statusText}`);
            }
    
           const result = await response.json();
            console.log('Create/Update result:', result);
            return result;
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            setPlanUpdating({ isUpdating: false, idPlan: '' });
        }
    }, []);


    return { plans, prompts, loading, error, sendTest, updatePrompt, planUpdating, createOrUpdatePlan };
};

export default useSOCopilotAdminPrompts;
